import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { toast } from 'react-hot-toast'
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react'
import Currency from '../components/Currency'
import { getFeaturedImageUrl } from '../utils/imageUtils'
import '../styles/CartCheckout.css'

const Cart = () => {
  const { items, total, itemCount, loading, updateCartItem, removeFromCart } = useCart()
  const { isAuthenticated } = useAuth()

  // Promo code state
  const [promoCode, setPromoCode] = useState('')
  const [promoDiscount, setPromoDiscount] = useState(0)
  const [promoError, setPromoError] = useState('')
  const [applyingPromo, setApplyingPromo] = useState(false)

  // Handle loading state and ensure cart items exist
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">جاري تحميل السلة...</p>
        </div>
      </div>
    )
  }

  if (!items || items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">سلة التسوق فارغة</h3>
          <p className="mt-1 text-sm text-gray-500">
            ابدأ بإضافة بعض المنتجات إلى سلتك!
          </p>
          <div className="mt-6">
            <Link
              to="/products"
              className="btn btn-primary"
            >
              متابعة التسوق
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Use the updateCartItem function with the correct parameter name
  const updateQuantity = (itemId, quantity) => {
    updateCartItem(itemId, quantity)
  }

  const getCartTotal = () => {
    return total || 0
  }

  const cartTotal = getCartTotal()

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('الرجاء إدخال كود خصم')
      return
    }

    setApplyingPromo(true)
    setPromoError('')
    
    try {
      const response = await fetch(`/api/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: promoCode,
          total: total 
        }),
      })
      
      const data = await response.json()
      
      if (response.ok) {
        setPromoDiscount(data.discount)
        toast.success(`تم تطبيق كود الخصم! ${data.discount_type === 'percentage' ? `${data.discount_value}%` : `$${data.discount.toFixed(2)}`} خصم`)
      } else {
        setPromoError(data.message || 'كود خصم غير صحيح')
        setPromoDiscount(0)
      }
    } catch (error) {
      console.error('Error applying promo code:', error)
      setPromoError('فشل في تطبيق كود الخصم')
      setPromoDiscount(0)
    } finally {
      setApplyingPromo(false)
    }
  }

  const handleRemovePromo = () => {
    setPromoCode('')
    setPromoDiscount(0)
    setPromoError('')
  }

  const finalTotal = total - promoDiscount

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">سلة التسوق</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-8">
          <div className="cart-section-card">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">منتجات السلة</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-6">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 border-b pb-6 last:border-b-0 last:pb-0">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                        {getFeaturedImageUrl(item.featured_image) ? (
                          <img
                            src={getFeaturedImageUrl(item.featured_image)}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <span className="text-gray-500 text-xs">لا توجد صورة</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 px-3">
                      <h4 className="text-lg font-medium text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-500">{item.short_description}</p>
                      <p className="text-lg font-medium text-gray-900 mt-2">
                        <Currency amount={parseFloat(item.display_price)} />
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          if (item.quantity === 1) {
                            removeFromCart(item.id)
                          } else {
                            updateCartItem(item.id, item.quantity - 1)
                          }
                        }}
                        className="p-1 rounded-full btn btn-secondary hover:bg-gray-500 sm:p-2"
                      >
                        <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <span className="w-8 sm:w-12 text-center font-medium text-sm sm:text-base">{item.quantity}</span>
                      <button
                        onClick={() => updateCartItem(item.id, item.quantity + 1)}
                        className="p-1 rounded-full btn btn-secondary hover:bg-gray-500 sm:p-2"
                      >
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                    <div className="text-lg font-medium text-gray-900">
                      <Currency amount={parseFloat(item.display_price) * item.quantity} />
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-1 sm:p-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cart Summary */}
        <div className="lg:col-span-4">
          <div className="card">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">ملخص الطلب</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">المجموع الفرعي</span>
                  <span className="font-medium"><Currency amount={total} /></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الشحن</span>
                  <span className="font-medium">مجاني</span>
                </div>
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>الخصم</span>
                    <span className="font-medium">-<Currency amount={promoDiscount} /></span>
                  </div>
                )}
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-medium">
                    <span>المجموع</span>
                    <span><Currency amount={finalTotal} /></span>
                  </div>
                </div>
              </div>

              {/* Promo Code Section */}
              <div className="mt-4 pt-4 border-t">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-900">كود الخصم</h3>
                  {promoDiscount > 0 ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center">
                        <span className="text-sm text-green-700 font-medium">"{promoCode}" تم تطبيقه!</span>
                      </div>
                      <button
                        onClick={handleRemovePromo}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        إزالة
                      </button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="أدخل كود الخصم"
                        className="flex-1 input text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                      />
                      <button
                        onClick={handleApplyPromo}
                        disabled={applyingPromo || !promoCode.trim()}
                        className="btn btn-secondary btn-sm"
                      >
                        {applyingPromo ? 'جاري التطبيق...' : 'تطبيق'}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-sm text-red-600">{promoError}</p>
                  )}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <Link
                  to="/checkout"
                  className="w-full btn btn-primary btn-lg"
                >
                  متابعة للدفع
                </Link>
                <Link
                  to="/products"
                  className="w-full btn btn-secondary btn-lg"
                >
                  متابعة التسوق
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Cart
